import React, { useState } from "react";
import { usePostHog } from 'posthog-js/react';
import { captureEvent, captureError } from "../utils/posthogUtils";

const ResetPasswordModal = ({ show, email, onClose }) => {
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const posthog = usePostHog();

  if (!show) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}api/auth/verify-reset`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: email.trim(),
            otp: otp.toString().trim(),
            newPassword,
          }),
        }
      );
      const data = await res.json();
      if (res.ok) {
        captureEvent(posthog, 'Password Reset Completed', { email });
        setMessage("Password reset successful! You can now log in.");
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        captureError(posthog, 'Password Reset Failed', new Error(data.message || "Error resetting password"), { email });
        setMessage(data.message || "Error resetting password.");
      }
    } catch (err) {
      captureError(posthog, 'Password Reset Failed', err, { email });
      setMessage("Server error.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div
        className="modal-content"
        style={{
          padding: 0,
          borderRadius: "24px",
          boxShadow:
            "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.05)",
          maxWidth: "480px",
          width: "90%",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: "linear-gradient(135deg, #6c757d 0%, #868e96 100%)",
            padding: "32px 32px 24px 32px",
            color: "white",
            textAlign: "center",
            position: "relative",
          }}
        >
          <h2
            style={{
              fontSize: "1.75rem",
              fontWeight: "700",
              margin: "0",
              letterSpacing: "-0.025em",
            }}
          >
            Reset Password
          </h2>
          <p
            style={{
              fontSize: "0.95rem",
              margin: "8px 0 0 0",
              opacity: "0.9",
              lineHeight: "1.4",
            }}
          >
            Enter the OTP sent to your email and create a new password.
          </p>
        </div>

        {/* Content */}
        <div style={{ padding: "32px", background: "#fafbfc" }}>
          <form
            onSubmit={handleSubmit}
            style={{ display: "flex", flexDirection: "column", gap: "20px" }}
          >
            {/* OTP Input */}
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              <label
                htmlFor="reset-otp"
                style={{
                  fontSize: "0.9rem",
                  fontWeight: "600",
                  color: "#374151",
                  marginBottom: "4px",
                }}
              >
                OTP Code
              </label>
              <input
                id="reset-otp"
                type="text"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
                style={{
                  padding: "12px 16px",
                  border: "2px solid #e5e7eb",
                  borderRadius: "12px",
                  fontSize: "1rem",
                  transition: "all 0.2s ease",
                  backgroundColor: "#fff",
                  outline: "none",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#6c757d";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(108, 117, 125, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e5e7eb";
                  e.target.style.boxShadow = "none";
                }}
                autoComplete="one-time-code"
              />
            </div>

            {/* New Password Input */}
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              <label
                htmlFor="reset-password"
                style={{
                  fontSize: "0.9rem",
                  fontWeight: "600",
                  color: "#374151",
                  marginBottom: "4px",
                }}
              >
                New Password
              </label>
              <input
                id="reset-password"
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                style={{
                  padding: "12px 16px",
                  border: "2px solid #e5e7eb",
                  borderRadius: "12px",
                  fontSize: "1rem",
                  transition: "all 0.2s ease",
                  backgroundColor: "#fff",
                  outline: "none",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#6c757d";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(108, 117, 125, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e5e7eb";
                  e.target.style.boxShadow = "none";
                }}
                autoComplete="new-password"
              />
            </div>

            {/* Buttons */}
            <div style={{ display: "flex", gap: "12px", marginTop: "8px" }}>
              <button
                type="submit"
                disabled={loading}
                style={{
                  flex: 1,
                  padding: "12px 24px",
                  background:
                    "linear-gradient(135deg, #6c757d 0%, #868e96 100%)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "12px",
                  fontSize: "1rem",
                  fontWeight: "600",
                  cursor: loading ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                  opacity: loading ? 0.7 : 1,
                  boxShadow: "0 2px 8px rgba(108, 117, 125, 0.2)",
                }}
                onMouseOver={(e) => {
                  if (!loading) {
                    e.target.style.transform = "translateY(-1px)";
                    e.target.style.boxShadow =
                      "0 4px 12px rgba(108, 117, 125, 0.3)";
                  }
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow =
                    "0 2px 8px rgba(108, 117, 125, 0.2)";
                }}
              >
                {loading ? "Resetting..." : "Reset Password"}
              </button>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: "12px 24px",
                  background: "#fff",
                  color: "#6c757d",
                  border: "2px solid #6c757d",
                  borderRadius: "12px",
                  fontSize: "1rem",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseOver={(e) => {
                  e.target.style.background = "#6c757d";
                  e.target.style.color = "#fff";
                }}
                onMouseOut={(e) => {
                  e.target.style.background = "#fff";
                  e.target.style.color = "#6c757d";
                }}
              >
                Cancel
              </button>
            </div>
          </form>

          {message && (
            <div
              style={{
                marginTop: "16px",
                padding: "12px 16px",
                borderRadius: "8px",
                fontSize: "0.9rem",
                fontWeight: "500",
                backgroundColor: message.includes("successful")
                  ? "#d1fae5"
                  : "#fee2e2",
                color: message.includes("successful") ? "#065f46" : "#dc2626",
                border: `1px solid ${message.includes("successful") ? "#a7f3d0" : "#fecaca"
                  }`,
              }}
            >
              {message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordModal;
